/**
 * MILESTONES, DRAWN — the small presentational pieces the roll-up cell, the
 * project report and the timeline lens share, so a milestone reads the same
 * wherever it appears. All the derivation is in src/milestones.ts; these only
 * render what it derived, to the approved mocks (dev-preview/portfolio-analytics).
 */
import type { Milestone, MilestoneState } from '../../milestones';
import type { DeliverySnapshot } from '../../types';
import type { Forecast } from '../../insights';
import Spark from './Spark';

import { MONTHS } from '../../when';
import { addDays } from '../../sprints';
/** A plain `YYYY-MM-DD` → "20 Sep", or "—" when absent/unparseable. */
export function fmtDay(iso?: string): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '—';
  return `${d} ${MONTHS[m - 1]}`;
}

/** State → the strip's colour class, shared with the health strip so a green
 *  milestone and a green pulse are the exact same green. Done reads as settled
 *  (green); on-track green, slipping amber, late red. */
export const MS_TONE: Record<MilestoneState, 'g' | 'a' | 'r'> = {
  'on-track': 'g',
  slipping: 'a',
  late: 'r',
  done: 'g',
};
/** The plain word for a milestone's slip state, the committee's terms. */
export const MS_WORD: Record<MilestoneState, string> = {
  'on-track': 'On track',
  slipping: 'Slipping',
  late: 'Late',
  done: 'Done',
};

/**
 * THE MILESTONE DIAMOND — a milestone is a zero-duration point, drawn as a
 * diamond so it never reads as a phase (a bar). Coloured by slip state; the
 * go-live is larger and ringed — the one date each project is judged on.
 */
export function MilestoneDiamond({
  state,
  ringed = false,
  size = 12,
  tone,
}: {
  state: MilestoneState;
  ringed?: boolean;
  size?: number;
  /** Override the state's colour — the Predicted lens tones by the PACE slip (fcTone),
   *  and a point with no linked work reads neutral ('muted'). Omitted → MS_TONE[state]. */
  tone?: 'g' | 'a' | 'r' | 'muted';
}) {
  return (
    <span className={`ms-dia ${tone ?? MS_TONE[state]}${ringed ? ' ring' : ''}`} aria-hidden="true">
      <svg width={size} height={size} viewBox="0 0 16 16">
        {ringed && <path className="ms-ring" d="M8 0.5 L15.5 8 L8 15.5 L0.5 8 Z" />}
        <path className="ms-fill" d="M8 3 L13 8 L8 13 L3 8 Z" />
      </svg>
    </span>
  );
}

/** The schedule tone for a PREDICTED finish — days the 85%-confident date (p85) lands
 *  past the plan, on the exact cut the go-live schedule RAG uses (over three weeks late
 *  reads red, any slip amber, on or before plan green). Measured on p85, the date every
 *  forecast surface now leads with (Vinnie, 29 Oct), so the tone matches the date shown.
 *  No plan to judge against → green (nothing to be late against). */
/** A plain YYYY-MM-DD as its LOCAL midnight instant — the app's convention for a plan day
 *  (ProjectStatus `planMsOf`, milestone-forecast `planMs`). Parsing it as UTC made the slip
 *  read a day high west of UTC, disagreeing with the report's own "N days late" headline
 *  (F1 / ledger A, 29 Oct). */
export const planLocalMs = (iso: string): number => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1).getTime();
};

export function fcTone(f: Forecast, plannedDay?: string): 'g' | 'a' | 'r' {
  if (!plannedDay) return 'g';
  const slip = Math.round((f.p85 - planLocalMs(plannedDay)) / 86_400_000);
  return slip > 21 ? 'r' : slip > 0 ? 'a' : 'g';
}

/**
 * THE CONFIDENCE MICRO-BAR — the p10–p95 range as a quiet track, filled to the 85%
 * headline and ticked at the p50 coin-flip. A decision-tier read, not a chart: "the
 * likely window, and where the middle sits", at a glance. Toned with the forecast ink.
 */
function ConfidenceBar({ f, tone }: { f: Forecast; tone: 'g' | 'a' | 'r' }) {
  const span = Math.max(1, f.p95 - f.p10);
  const pct = (x: number) => Math.max(0, Math.min(100, ((x - f.p10) / span) * 100));
  return (
    <span className={`cad-track ${tone}`} aria-hidden="true">
      <span className="cad-fill" style={{ width: `${pct(f.p85)}%` }} />
      <span className="cad-p50" style={{ left: `${pct(f.p50)}%` }} />
    </span>
  );
}

/**
 * THE ROLL-UP'S NEXT-MILESTONE CELL — the nearest committee-grade milestone as a
 * diamond chip, in the column the go-live date used to occupy. It answers "what's the
 * next thing this project is judged on, and WHEN will it actually land?" in one glance.
 * When the milestone gates real work and the board has enough finished history, the
 * quiet lines read a PREDICTED finish from the board's cadence — the 85%-confident date
 * (p85) as the headline, with the 50/50 midpoint beside a confidence micro-bar. Too
 * little history degrades honestly to the plan with "not enough history yet"; a point
 * with no tie keeps the plan read.
 */
export function NextMilestoneCell({ next, onHold }: { next?: Milestone; onHold?: boolean }) {
  if (onHold) return <div className="pf-ms"><div className="ms-when held">Paused</div></div>;
  if (!next) {
    return (
      <div className="pf-ms">
        <div className="ms-name none">No milestone set</div>
      </div>
    );
  }
  const head = (
    <div className="ms-head">
      <MilestoneDiamond state={next.state} ringed={next.kind === 'golive'} />
      <span className="ms-name">{next.name}</span>
    </div>
  );
  const f = next.forecast;

  // A predicted finish with its confidence — the steering read, from the board's pace.
  if (f && f.ready) {
    const tone = fcTone(f, next.plannedDay);
    const planLate = !!(next.plannedDay && f.p85 > planLocalMs(next.plannedDay));
    return (
      <div className="pf-ms">
        {head}
        <div className={`ms-when ${tone}`}>
          {planLate && next.plannedDay
            ? <>was {fmtDay(next.plannedDay)} → <b>{fmtMs(f.p85)}</b></>
            : <><b>{fmtMs(f.p85)}</b> · on plan</>}
        </div>
        <div className="cad-conf">
          <ConfidenceBar f={f} tone={tone} />
          <span className="cad-by"><b>85%</b> confident · 50/50 ~{fmtMs(f.p50)}</span>
        </div>
      </div>
    );
  }

  // Scoped, but too little board history to predict honestly — show the plan, say so.
  if (f && !f.ready) {
    return (
      <div className="pf-ms">
        {head}
        <div className={`ms-when ${MS_TONE[next.state]}`}>
          {next.state === 'late'
            ? <>{fmtDay(next.plannedDay)} · <b>{next.slipDays}d late</b></>
            : <>{fmtDay(next.plannedDay ?? next.forecastDay)} planned</>}
        </div>
        <div className="cad-cold">Not enough history yet</div>
      </div>
    );
  }

  // No scope to forecast (a standalone point with no tie). An overdue or drifting one is
  // honestly late/slipping; an on-track one has NOTHING tied to predict from, so it must
  // not read "on plan" — false reassurance for a checkpoint nobody has forecast. It says
  // "no linked work" instead (Vinnie, 29 Oct), consistent with the milestone table.
  const drifted = !!(next.plannedDay && next.forecastDay && next.forecastDay > next.plannedDay);
  if (next.state === 'late' || next.state === 'slipping') {
    return (
      <div className="pf-ms">
        {head}
        <div className={`ms-when ${MS_TONE[next.state]}`}>
          {next.state === 'late' && !drifted
            // Overdue but not re-forecast — the drift arrow would read "→ same day".
            ? <>{fmtDay(next.plannedDay)} · <b>{next.slipDays}d late</b></>
            : <>{fmtDay(next.plannedDay)} → <b>{fmtDay(next.forecastDay)}</b></>}
        </div>
      </div>
    );
  }
  return (
    <div className="pf-ms">
      {head}
      <div className="ms-when"><span className="muted">{fmtDay(next.plannedDay ?? next.forecastDay)} · no linked work</span></div>
    </div>
  );
}

/** A go-live snapshot's ms date → "14 Oct" (the roll-up's short worded day),
 *  using the same month names the ISO formatter above does. */
function fmtMs(ms: number): string {
  const d = new Date(ms);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/**
 * THE AS-DELIVERED CELL — the delivered twin of NextMilestoneCell, shown in the
 * same "Next milestone" column for a handed-over SERVICE row (#435, Vinnie
 * 22 Oct: "surface the as-delivered snapshot on the grid too"). Where a board
 * still in delivery shows the UPCOMING go-live (plan → forecast), a board now in
 * service shows the record frozen at handoff: the go-live it actually hit
 * (planned → actual, the schedule variance in its tone), and what shipped vs was
 * carried into service. The money at go-live rides the Budget column, so this
 * cell keeps the delivery story — dates and scope — together.
 */
export function DeliveredCell({ snap }: { snap: DeliverySnapshot }) {
  const actual = fmtMs(snap.actualGoLive);
  const planned = snap.plannedGoLive != null ? fmtMs(snap.plannedGoLive) : null;
  const v = snap.scheduleVarianceDays;
  // Tone the record by how the go-live landed: on plan or early reads settled
  // (green); late reads amber, and badly late (past the fortnight line) red —
  // the same honesty the steering pack prints.
  let tone: 'g' | 'a' | 'r' = 'g';
  let when: JSX.Element;
  if (v == null || planned == null) {
    when = <>Went live {actual}</>;
  } else if (v > 0) {
    tone = v > 14 ? 'r' : 'a';
    when = <>{planned} → <b>{actual}</b> · {v}d late</>;
  } else if (v < 0) {
    when = <>{planned} → {actual} · {-v}d early</>;
  } else {
    when = <>{planned} → {actual} · on plan</>;
  }
  const diaState: MilestoneState = tone === 'r' ? 'late' : tone === 'a' ? 'slipping' : 'done';
  const scope =
    snap.scopeTotal > 0
      ? `${snap.scopeShipped} of ${snap.scopeTotal} shipped${snap.scopeDeferred > 0 ? ` · ${snap.scopeDeferred} deferred` : ''}`
      : null;
  return (
    <div className="pf-ms pf-delivered">
      <div className="ms-head">
        <MilestoneDiamond state={diaState} ringed />
        <span className="ms-name">Went live{snap.provisional ? ' · provisional' : ''}</span>
      </div>
      <div className={`ms-when ${MS_TONE[diaState]}`}>{when}</div>
      {scope ? <div className="ms-scope">{scope}</div> : null}
    </div>
  );
}

/** A small rising arrow for the Slip column — slip growing is always "up and to
 *  the right", so the direction reads the same whatever the colour. */
function SlipArrow() {
  return (
    <svg className="ms-slip-arw" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 18L18 6" />
      <path d="M9 6h9v9" />
    </svg>
  );
}

/** How a milestone's slip reads in the table's Slip column: "on time" / "on plan"
 *  when there is no slip, or a rising "+Nd" coloured by state. Done-but-late keeps
 *  the arrow (it landed late) in amber; a still-open late one is red. */
function slipReadout(m: Milestone): { text: string; tone: 'g' | 'a' | 'r'; arrow: boolean } {
  if (m.state === 'done') {
    return m.slipDays > 0 ? { text: `+${m.slipDays}d`, tone: 'a', arrow: true } : { text: 'on time', tone: 'g', arrow: false };
  }
  if (m.state === 'on-track') return { text: 'on plan', tone: 'g', arrow: false };
  return { text: `+${m.slipDays}d`, tone: m.state === 'late' ? 'r' : 'a', arrow: true };
}

/**
 * THE MILESTONES TABLE — planned → forecast → actual, one row per milestone, with
 * a Slip column that does the arithmetic so no one subtracts dates in their head.
 * A diamond carries the state; the go-live is ringed.
 */
export function MilestoneTable({ milestones }: { milestones: Milestone[] }) {
  return (
    <table className="ms-table">
      <thead>
        <tr>
          <th>Milestone</th>
          <th>Planned</th>
          <th>Predicted</th>
          <th>Actual</th>
          <th className="r">Slip vs plan</th>
        </tr>
      </thead>
      <tbody>
        {milestones.map((m) => {
          const f = m.forecast;
          const ptone = f && f.ready ? fcTone(f, m.plannedDay) : undefined;
          const planMs = m.plannedDay ? planLocalMs(m.plannedDay) : undefined;
          const hasPred = !!(f && f.ready && planMs !== undefined);
          // Predicted-vs-plan slip, so a late checkpoint still reads late — the plan is
          // the baseline, the 85%-confident date (p85) the honest finish this report leads
          // with. A done row keeps its own actual-vs-plan slip.
          const predSlip = hasPred ? Math.max(0, Math.round((f!.p85 - planMs!) / 86_400_000)) : undefined;
          // A milestone with NO cadence forecast that is not already overdue has nothing to
          // predict from — it must never read "on plan" (false reassurance for a checkpoint
          // nobody has forecast). A forecast that exists but isn't ready is a cold-start
          // ("building"); none at all is an untied/no-scope point ("no linked work"). An
          // overdue one (slipDays > 0) is honestly late regardless, and keeps its plan+slip.
          const unforecast = m.state !== 'done' && !hasPred && m.slipDays <= 0;
          const s: { text: string; tone: '' | 'g' | 'a' | 'r'; arrow: boolean; muted?: boolean } =
            m.state === 'done'
              ? slipReadout(m)
              : hasPred
                ? (predSlip! > 0 ? { text: `+${predSlip!}d`, tone: ptone ?? 'a', arrow: true } : { text: 'on plan', tone: 'g', arrow: false })
                : unforecast
                  ? { text: '—', tone: '', arrow: false, muted: true }
                  : slipReadout(m);
          return (
            <tr key={m.key}>
              <td className="ms-cell-name">
                <MilestoneDiamond state={m.state} ringed={m.kind === 'golive'} />
                <span>{m.name}</span>
              </td>
              <td className="ms-num">{fmtDay(m.plannedDay)}</td>
              <td className="ms-num">
                {/* The predicted finish from the board's cadence: lead with the 85%-confident
                    date (p85), with the 50/50 midpoint (p50) as context — the same headline
                    the drill band and the big forecast panel use (Vinnie, 29 Oct). An overdue
                    milestone with no forecast shows the earliest it can still land — plan +
                    slip (Vinnie, 26 Oct). A milestone that simply CANNOT be forecast and is
                    not late says so plainly — "no linked work" (no scope tie) or "building —
                    not enough history" (cold-start) — never a plan date dressed as a
                    prediction, which would read "on plan" for something nobody has forecast
                    (Vinnie, 29 Oct). A done row shows "done". */}
                {m.actualDay
                  ? <span className="ms-pred-done">done</span>
                  : hasPred
                    ? <span className={`ms-pred ${ptone}`}><b>{fmtMs(f!.p85)}</b><span className="ms-pred-by">85% confident · 50/50 ~{fmtMs(f!.p50)}</span></span>
                    : unforecast
                      ? <span className="muted">{f ? 'building — not enough history' : 'no linked work'}</span>
                      : fmtDay(m.plannedDay && m.slipDays > 0 ? addDays(m.plannedDay, m.slipDays) : (m.forecastDay ?? m.plannedDay))}
              </td>
              <td className="ms-num">{m.actualDay ? fmtDay(m.actualDay) : '—'}</td>
              <td className={`ms-num ms-slip ${s.tone}`}>
                {s.arrow && <SlipArrow />}{s.muted ? <span className="muted">{s.text}</span> : s.text}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * THE SLIP-TREND LINE — the leapfrog. The daily pulse snapshots each milestone's
 * forecast; plotted over the recorded days, a rising line means the finish is
 * running away. The chart is the evidence, the sentence beside it the answer.
 * Reads "building" until two days have been recorded.
 */
/** The daily snapshot runs every day, so a newest point older than this means the writer has
 *  stopped — the line is frozen, and a frozen flat line must NOT keep reassuring "on track". */
const TREND_STALE_DAYS = 10;

export function SlipTrendLine({
  label,
  series,
  now = Date.now(),
}: {
  label: string;
  series: { day: string; slipDays: number }[];
  now?: number;
}) {
  if (series.length < 2) {
    return (
      <div className="ms-trend">
        <div className="ms-trend-head">{label} — forecast trend</div>
        <div className="ms-trend-say">Building — the daily snapshot needs a day or two before a trend shows.</div>
      </div>
    );
  }
  const first = series[0].slipDays;
  const last = series[series.length - 1].slipDays;
  const lastDay = series[series.length - 1].day;
  // A frozen trend looks healthy forever. Gate the reassurance on the newest snapshot's age, so a
  // stopped writer reads "paused", never "on track".
  const staleDays = Math.max(0, Math.floor((now - Date.parse(`${lastDay}T00:00:00Z`)) / 86_400_000));
  const stale = staleDays > TREND_STALE_DAYS;
  const dir = stale ? 'paused' : last > first ? 'slipping' : last < first ? 'recovering' : 'steady';
  const tone = stale ? 'w' : dir === 'slipping' ? 'r' : dir === 'recovering' ? 'g' : '';
  const drift = last;
  return (
    <div className="ms-trend">
      <div className="ms-trend-head">
        {label} — last {series.length} weekly snapshots
        <span className={`ms-trend-word ${tone}`}>{dir}</span>
      </div>
      <div className="ms-trend-body">
        <Spark values={series.map((s) => s.slipDays)} w={150} h={40} />
        <span className={`ms-trend-say${stale ? ' stale' : ''}`}>
          {stale ? (
            <>Trend paused — the last snapshot was {staleDays} days ago, so this line may be out of date.</>
          ) : drift > 0 ? (
            <>The forecast has drifted <b>+{drift} days</b> over {series.length} snapshots — {dir === 'slipping' ? 'a steadily rising line, not a one-off' : 'now easing back'}. Flat would be on plan.</>
          ) : (
            <>The forecast has held on plan across {series.length} snapshots — a flat line is on track.</>
          )}
        </span>
      </div>
    </div>
  );
}
